import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useContext } from "react";
import { useNavigate } from "react-router";
import { administratorMethods } from "../api/apiMethods";
import { canManageAdministrators } from "../common/helpers/municipalityRoles";
import { AuthenticatedUserContext } from "../contexts/AuthenticatedUserContext";
import { useQueryErrorHandling } from "../hooks/useQueryErrorHandling";
import useSelectedMunicipalityId from "../hooks/useSelectedMunicipalityId";
import { AdministratorListResponse } from "../views/AdministratorView/administratorTypes";
import { queryKeys } from "./queryKeys";

export const useAdministratorByIdQuery = (
    administratorId?: string,
    options?: {
        enabled: boolean;
    }
) => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const { authUserData } = useContext(AuthenticatedUserContext);
    const municipalityId = useSelectedMunicipalityId();

    const query = useQuery({
        queryKey: queryKeys.administrator.byId(municipalityId, administratorId),
        queryFn: () => {
            if (!authUserData || !administratorId) {
                throw new Error("No auth user data");
            }

            if (!municipalityId) {
                throw new Error("No municipality Id");
            }

            try {
                return administratorMethods(municipalityId).getAdministrator(
                    administratorId
                );
            } catch (error) {
                throw new Error("Could not get administrator data");
            }
        },
        enabled:
            !!authUserData &&
            canManageAdministrators(authUserData.municipalityRole) &&
            !!administratorId &&
            (!options || options.enabled),
        initialData: () =>
            (
                queryClient.getQueryData(
                    queryKeys.administrator.all(municipalityId)
                ) as AdministratorListResponse | undefined
            )?.userlist?.find(
                (administrator) => administrator.uid === administratorId
            ),
    });

    useQueryErrorHandling(
        query,
        "Kunde inte hämta uppgifter för denna administratör.",
        () => {
            navigate(-1);
        }
    );

    return query;
};
